import type { ManagementTimelineMarker } from './management-timeline';
import type {
  ControlPlaneFinalizationProjection,
  ControlPlaneUsageCollectorProjection,
  ControlPlaneUsageRecoveryProjection,
} from './control-plane-usage-reliability';

/**
 * PISO ABSOLUTO de versão do CLI Claude Code aceito por binários EXTERNOS
 * (Anthropic standalone / PATH / npm global) — abaixo dele o CLI é considerado
 * velho demais para funcionar com o SDK.
 *
 * IMPORTANTE: "compatível com o piso" NÃO é o mesmo que "casado com o SDK".
 * A paridade SDK↔CLI exige a versão exata casada ({@link CLAUDE_AGENT_SDK_VERSION},
 * obtida pelo binário nativo do SDK ou pelo managed dir baixado no bootstrap).
 * Um CLI externo acima do piso ainda pode driftar — por isso o caminho casado
 * (managed/nativo do SDK) é preferido e o worker avisa quando recorre a um CLI
 * externo. Ver .spassu/archive/legacy-initiatives/docs/standalone-plans/projeto/plano-adequacao-sdk-cli.md (histórico).
 *
 * Source of truth para o TS side (worker e backend que
 * eventualmente precise validar a versão). O Tauri Rust (engines.rs) mantém
 * uma const local sincronizada — há um teste no Rust que lê este valor
 * direto do contract.ts e falha se divergir.
 *
 * Override em runtime: env `SPASSU_CLAUDE_MIN_VERSION` ou app-config.yaml
 * (`spassuCode.claude.minimumVersion`, consumido pelo backend Backstage e
 * propagado ao worker via env quando aplicável).
 */
export const CLAUDE_MINIMUM_VERSION = '2.1.112';

/**
 * Versão exata do `@anthropic-ai/claude-agent-sdk` adotada pelo projeto.
 *
 * Fonte única da verdade para a PARIDADE SDK↔CLI. O runtime do SDK só é
 * compatível com o binário nativo do CLI publicado na MESMA versão
 * (`@anthropic-ai/claude-agent-sdk-<plataforma>@<esta-versão>`). Dirigir um CLI
 * de versão diferente pode causar mismatch de protocolo de controle e crash
 * nativo (SIGSEGV) — ver .spassu/archive/legacy-initiatives/docs/standalone-plans/projeto/plano-adequacao-sdk-cli.md (historico) e
 * docs/projeto/plugins/frontier/worker/operacao-e-diagnostico.md.
 *
 * Onde é usada:
 *  - deve casar com o pin de `@anthropic-ai/claude-agent-sdk` no package.json
 *    do worker/backend (há teste de sync que falha se divergir);
 *  - o bootstrap do Worker Desktop (Tauri) baixa o tarball nativo desta versão
 *    na primeira execução, em vez do `latest` do instalador oficial.
 *
 * Ao bumpar o SDK: atualizar este valor e os package.json do worker/backend.
 */
export const CLAUDE_AGENT_SDK_VERSION = '0.3.222';

/**
 * Modelos Claude expostos pelo worker (`getAvailableModels`).
 * Fonte única — não duplicar a lista em cada engine. A disponibilidade real
 * depende do CLI/conta; esta lista reflete os modelos suportados no Claude Code
 * 2.1.x adotado pelo projeto.
 */
export const CLAUDE_AVAILABLE_MODELS = [
  'claude-opus-4-7',
  'claude-opus-4-6',
  'claude-sonnet-4-5-20250929',
  'claude-haiku-4-5-20251001',
] as const;

/**
 * Modelo padrão quando nenhum é especificado (worker `DEFAULT_MODEL` e backend
 * `FALLBACK_MODEL`). Não confundir com `spassuCode.models.default` do
 * app-config, que é o default de configuração de fases.
 */
export const CLAUDE_DEFAULT_MODEL = 'claude-opus-4-7';

/**
 * Modelos Codex expostos pelo worker (`LocalCodexSessionManager.getAvailableModels`)
 * e pelo catálogo do Worker Desktop. Fonte única — não duplicar por engine. A
 * disponibilidade real depende da conta/auth (`OPENAI_API_KEY` ou
 * `~/.codex/auth.json`). Ver .spassu/archive/legacy-initiatives/docs/standalone-plans/projeto/plano-multi-engine.md (histórico).
 */
export const CODEX_AVAILABLE_MODELS = ['o4-mini', 'o3', 'gpt-4.1'] as const;

/** Modelo Codex padrão quando nenhum é especificado. */
export const CODEX_DEFAULT_MODEL = 'o4-mini';

/** Default efetivo do paralelismo de tasks. Mantido sequencial ate o merge seguro. */
export const DEFAULT_MAX_PARALLEL_TASKS = 1;

/** Teto defensivo para slots paralelos quando nao houver cap central menor. */
export const MAX_PARALLEL_TASKS_LIMIT = 16;

export * from './security-scanner';
export * from './task-breakdown-config';
export * from './cockpit';
export * from './cockpit-helpers';
export * from './management-timeline';
export * from './management-timeline-query';

export interface PersistedParallelTaskState {
  totalSlots?: number | null;
}

export function normalizeMaxParallelTasks(
  value: unknown,
  options: { max?: number } = {},
): number {
  const max = Number.isFinite(options.max)
    ? Math.max(DEFAULT_MAX_PARALLEL_TASKS, Math.floor(options.max as number))
    : MAX_PARALLEL_TASKS_LIMIT;
  const numericValue =
    typeof value === 'number' ? value : Number.parseFloat(String(value));

  if (
    !Number.isFinite(numericValue) ||
    numericValue < DEFAULT_MAX_PARALLEL_TASKS
  ) {
    return DEFAULT_MAX_PARALLEL_TASKS;
  }

  return Math.min(Math.floor(numericValue), max);
}

export function getPersistedMaxParallelTasks(
  tasks: PersistedParallelTaskState[],
): number {
  const maxTotalSlots = tasks.reduce((max, task) => {
    const totalSlots = task.totalSlots ?? DEFAULT_MAX_PARALLEL_TASKS;
    return Math.max(max, totalSlots);
  }, DEFAULT_MAX_PARALLEL_TASKS);

  return normalizeMaxParallelTasks(maxTotalSlots, {
    max: MAX_PARALLEL_TASKS_LIMIT,
  });
}

export function resolveEffectiveMaxParallelTasks(params: {
  requested?: unknown;
  fallbackMaxParallelTasks: number;
}): number {
  const operatorMaxParallelTasks = normalizeMaxParallelTasks(
    params.fallbackMaxParallelTasks,
    {
      max: MAX_PARALLEL_TASKS_LIMIT,
    },
  );

  if (params.requested === undefined) {
    return operatorMaxParallelTasks;
  }

  return normalizeMaxParallelTasks(params.requested, {
    max: operatorMaxParallelTasks,
  });
}

export function resolveResumeMaxParallelTasks(params: {
  requested?: unknown;
  persistedMaxParallelTasks: number;
  fallbackMaxParallelTasks: number;
}): number {
  if (params.requested !== undefined) {
    return resolveEffectiveMaxParallelTasks({
      requested: params.requested,
      fallbackMaxParallelTasks: params.fallbackMaxParallelTasks,
    });
  }

  const operatorMaxParallelTasks = normalizeMaxParallelTasks(
    params.fallbackMaxParallelTasks,
    {
      max: MAX_PARALLEL_TASKS_LIMIT,
    },
  );
  const persisted = normalizeMaxParallelTasks(
    params.persistedMaxParallelTasks,
    {
      max: operatorMaxParallelTasks,
    },
  );
  if (persisted > DEFAULT_MAX_PARALLEL_TASKS) {
    return persisted;
  }

  return DEFAULT_MAX_PARALLEL_TASKS;
}

/**
 * Nome do plugin de skills do Spassu Code, como declarado em
 * `.spassu/.claude-plugin/plugin.json` (`name`). O Claude Code expõe os
 * comandos de um plugin local NAMESPACED por esse nome — ex.: o arquivo
 * `commands/criar-prd.md` vira o slash command `/spassu-code:criar-prd`.
 *
 * Por isso o prompt enviado ao agente precisa invocar o comando namespaced
 * (`/spassu-code:criar-prd`), não o bare (`/criar-prd`) — senão o CLI não
 * reconhece o comando e encerra sem executar o skill (0 turnos). Ver a
 * documentacao operacional do worker Frontier.
 *
 * MANTER SINCRONIZADO com o `name` do `.claude-plugin/plugin.json`.
 */
export const CLAUDE_SKILLS_PLUGIN_NAME = 'spassu-code';

/**
 * Converte um slash command BARE (`/criar-prd`, `/executar-task`) no comando
 * NAMESPACED do plugin (`/spassu-code:criar-prd`), que é como o Claude Code
 * expõe comandos de um plugin local. Idempotente (não re-namespaceia).
 *
 * Use ao montar o PROMPT enviado ao agente. NÃO use para localizar o arquivo
 * `.md` do comando (esse usa o nome bare). Ver
 * docs/projeto/plugins/frontier/worker/operacao-e-diagnostico.md.
 */
export function toPluginSlashCommand(skill: string): string {
  const bare = skill.replace(/^\//, '');
  if (bare.length === 0 || bare.includes(':')) {
    return skill;
  }
  return `/${CLAUDE_SKILLS_PLUGIN_NAME}:${bare}`;
}

// ---------------------------------------------------------------------------
// AI Engine — abstração de "motor de IA" usada pelo worker. Decisão D0.1 em
// docs/worker-desktop/fase-0-pauta.md:
// usar `aiEngine` em vez de "provider" (que já significa coisas diferentes
// no projeto — git provider do ADR-049 e tipo de agente do Control Plane).
//
// Esta é a interface leve: metadados + capabilities + auth. Implementações
// concretas (LocalClaudeSessionManager e LocalCodexSessionManager) trazem o
// método de criar sessão com a assinatura que cada cenário precisa.
// ---------------------------------------------------------------------------

/** Engines suportados no v1 (D0.2). */
export type AIEngineName = 'claude-code' | 'codex';

/**
 * Capabilities por engine. Usadas para gates de fase (D0.4 — fases
 * skill-dependentes ficam bloqueadas em engines que não suportam skills
 * customizadas no v1).
 */
export interface AIEngineCapabilities {
  /** Suporta `.claude-plugin/plugin.json` (skills customizadas do Spassu Code). */
  supportsSkillsPlugin: boolean;
  /** Suporta servidor MCP customizado via SDK. */
  supportsMCP: boolean;
  /** Suporta tool use (function calling). */
  supportsToolUse: boolean;
  /** Stream incremental durante a resposta. */
  supportsStreaming: boolean;
}

/**
 * Modo de autenticação usado pela engine. Decide como o usuário "paga" pelo
 * consumo. Detectado em ordem de prioridade definida pelo session manager.
 */
export type AIEngineAuthMode = 'subscription' | 'api-key' | 'env';

/** Resultado de `isAuthenticated()` — informa o modo ativo para a UI mostrar. */
export interface AIEngineAuthStatus {
  authenticated: boolean;
  /** Qual fonte autenticou. `null` quando não autenticado. */
  activeMode: AIEngineAuthMode | null;
}

/** Identidade da engine — metadados que o backend e a UI consultam. */
export interface AIEngineMetadata {
  readonly name: AIEngineName;
  readonly capabilities: AIEngineCapabilities;
}

/**
 * Contrato leve que session managers implementam para serem reconhecíveis
 * como AIEngine. Foco: identidade + capabilities + status de auth.
 *
 * Operações específicas (`startSession()` no worker) ficam na implementação
 * concreta para manter o contract focado em identidade e capabilities.
 */
export interface AIEngine extends AIEngineMetadata {
  /**
   * Verifica se a engine está autenticada para uso. Implementações devem
   * checar na ordem: env var → keyring/token → estado OAuth do CLI nativo.
   * O `mode` opcional permite forçar uma fonte específica.
   */
  isAuthenticated(mode?: AIEngineAuthMode): Promise<AIEngineAuthStatus>;

  /** Modelos disponíveis para a engine. Pode bater na API ou retornar lista hardcoded. */
  getAvailableModels(): Promise<string[]>;
}

/**
 * Capabilities padrão para Claude Code. Implementações podem expor isso
 * direto via `capabilities` ao implementar `AIEngine`.
 */
export const CLAUDE_CODE_CAPABILITIES: AIEngineCapabilities = {
  supportsSkillsPlugin: true,
  supportsMCP: true,
  supportsToolUse: true,
  supportsStreaming: true,
};

/**
 * Capabilities padrão para Codex. Note `supportsSkillsPlugin: false` —
 * Codex CLI não consome `.claude-plugin/plugin.json` (D0.4). UI deve
 * bloquear fases skill-dependentes (`dividindo-tasks`, `executando`,
 * `pronto`) com aviso quando engine ativa é `codex`.
 */
export const CODEX_CAPABILITIES: AIEngineCapabilities = {
  supportsSkillsPlugin: false,
  supportsMCP: false,
  supportsToolUse: true,
  supportsStreaming: true,
};

/** Lista de fases que dependem de skills customizadas (engine deve ter `supportsSkillsPlugin: true`). */
export const SKILL_DEPENDENT_PHASES: Phase[] = [
  'dividindo-tasks',
  'executando',
  'pronto',
];

/** Helper para gates de UI: dada engine + fase, retorna se pode executar. */
export function canEngineRunPhase(
  capabilities: AIEngineCapabilities,
  phase: Phase,
): boolean {
  if (
    SKILL_DEPENDENT_PHASES.includes(phase) &&
    !capabilities.supportsSkillsPlugin
  ) {
    return false;
  }
  return true;
}

/** Capabilities por NOME de engine — fonte única para resolver name→capabilities. */
export const AI_ENGINE_CAPABILITIES: Record<
  AIEngineName,
  AIEngineCapabilities
> = {
  'claude-code': CLAUDE_CODE_CAPABILITIES,
  codex: CODEX_CAPABILITIES,
};

/**
 * Gate por NOME de engine — conveniência para o frontend, que só conhece o
 * nome (`activity.aiEngine`) e não a instância. Resolve as capabilities pelo
 * mapa e delega a `canEngineRunPhase`. NÃO duplicar `SKILL_DEPENDENT_PHASES`
 * no frontend; usar este helper.
 */
export function canEngineNameRunPhase(
  engine: AIEngineName,
  phase: Phase,
): boolean {
  return canEngineRunPhase(AI_ENGINE_CAPABILITIES[engine], phase);
}

export type Phase =
  | 'gerando-prd'
  | 'gerando-techspec'
  | 'dividindo-tasks'
  | 'executando'
  | 'qa'
  | 'pronto'
  | 'finalizado';

export type ActivitySource = 'backstage' | 'cli-standalone' | 'cli-legacy';

export type ActivityExecutionMode = 'cli' | 'pipeline' | null;

export const ACTIVITY_WORK_PATHS = [
  'frontier-web',
  'cli-standalone',
  'control-plane',
] as const;

export type ActivityWorkPath = (typeof ACTIVITY_WORK_PATHS)[number];

/** Returns whether an unknown value is a canonical activity work path. */
export function isActivityWorkPath(value: unknown): value is ActivityWorkPath {
  return ACTIVITY_WORK_PATHS.some(workPath => workPath === value);
}

export const CREATABLE_ACTIVITY_WORK_PATHS = [
  'frontier-web',
  'control-plane',
] as const satisfies readonly ActivityWorkPath[];

export type CreatableActivityWorkPath =
  (typeof CREATABLE_ACTIVITY_WORK_PATHS)[number];

export const ACTIVITY_STATUSES = [
  'preparing',
  'active',
  'completed',
  'cancelled',
  'error',
] as const;

export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];

export function isActivityStatus(value: unknown): value is ActivityStatus {
  return ACTIVITY_STATUSES.some(status => status === value);
}

/** Keeps historical work-path parsing separate from new activity creation. */
export function isCreatableActivityWorkPath(
  value: unknown,
): value is CreatableActivityWorkPath {
  return CREATABLE_ACTIVITY_WORK_PATHS.some(workPath => workPath === value);
}

export const ACTIVITY_WORK_PATH_NOT_CREATABLE =
  'ACTIVITY_WORK_PATH_NOT_CREATABLE' as const;
export const ACTIVITY_WORK_PATH_NOT_CREATABLE_MESSAGE =
  'New activities are not available for this work path. Use frontier-web or control-plane.';

export type OnboardingConnectPath = 'control-plane-enriched' | 'cli-standalone';

export const ONBOARDING_REPO_CONTEXTS = ['existing', 'clone'] as const;

export type OnboardingRepoContext = (typeof ONBOARDING_REPO_CONTEXTS)[number];

export function isOnboardingRepoContext(
  value: unknown,
): value is OnboardingRepoContext {
  return ONBOARDING_REPO_CONTEXTS.some(context => context === value);
}

/**
 * Existing states remain valid while the secure lifecycle is adopted by each
 * consumer. New states are additive and distinguish connection, expiry,
 * revocation and completion instead of overloading `succeeded`.
 */
export const ONBOARDING_SESSION_STATUSES = [
  'generated',
  'validating',
  'connected',
  'succeeded',
  'failed',
  'expired',
  'revoked',
  'completed',
] as const;

export type OnboardingSessionStatus =
  (typeof ONBOARDING_SESSION_STATUSES)[number];

/**
 * Terminal subset of the onboarding session lifecycle: once a session reaches
 * one of these statuses no further automatic polling or transition applies.
 * Single canonical source — consumers must not redeclare this list locally.
 */
export const TERMINAL_ONBOARDING_SESSION_STATUSES = [
  'connected',
  'succeeded',
  'failed',
  'expired',
  'revoked',
  'completed',
] as const satisfies readonly OnboardingSessionStatus[];

export function isOnboardingSessionStatus(
  value: unknown,
): value is OnboardingSessionStatus {
  return ONBOARDING_SESSION_STATUSES.some(status => status === value);
}

export const ONBOARDING_BOOTSTRAP_STAGES = [
  'redeemed',
  'workspace_ready',
  'bundle_ready',
  'local_state_ready',
  'committed',
  'failed',
] as const;

export type OnboardingBootstrapStage =
  (typeof ONBOARDING_BOOTSTRAP_STAGES)[number];

export function isOnboardingBootstrapStage(
  value: unknown,
): value is OnboardingBootstrapStage {
  return ONBOARDING_BOOTSTRAP_STAGES.some(stage => stage === value);
}

export const ONBOARDING_ATTEMPT_STATES = [
  'in_progress',
  'succeeded',
  'failed',
] as const;

export type OnboardingAttemptState = (typeof ONBOARDING_ATTEMPT_STATES)[number];

export function isOnboardingAttemptState(
  value: unknown,
): value is OnboardingAttemptState {
  return ONBOARDING_ATTEMPT_STATES.some(state => state === value);
}

export const ONBOARDING_BOOTSTRAP_FAILURE_CODES = [
  'BOOTSTRAP_WORKSPACE_INVALID',
  'BOOTSTRAP_WORKSPACE_LOCKED',
  'BOOTSTRAP_BUNDLE_INVALID',
  'BOOTSTRAP_LOCAL_STATE_WRITE_FAILED',
  'BOOTSTRAP_NO_PROGRESS',
  'BOOTSTRAP_PROTOCOL_MISMATCH',
  'BOOTSTRAP_CUTOVER_REJECTED',
  'BOOTSTRAP_CLIENT_INTERRUPTED',
] as const;

export type OnboardingBootstrapFailureCode =
  (typeof ONBOARDING_BOOTSTRAP_FAILURE_CODES)[number];

export function isOnboardingBootstrapFailureCode(
  value: unknown,
): value is OnboardingBootstrapFailureCode {
  return ONBOARDING_BOOTSTRAP_FAILURE_CODES.some(code => code === value);
}

export const ONBOARDING_BOOTSTRAP_ACTION_CODES = [
  'resume_same_workspace',
  'generate_new_code',
  'inspect_workspace',
  'contact_support',
] as const;

export type OnboardingBootstrapActionCode =
  (typeof ONBOARDING_BOOTSTRAP_ACTION_CODES)[number];

export function isOnboardingBootstrapActionCode(
  value: unknown,
): value is OnboardingBootstrapActionCode {
  return ONBOARDING_BOOTSTRAP_ACTION_CODES.some(code => code === value);
}

export interface ControlPlaneBootstrapDiagnostic {
  code: OnboardingBootstrapFailureCode;
  actionCode: OnboardingBootstrapActionCode;
}

export interface OnboardingBootstrapDiagnosticDefinition
  extends ControlPlaneBootstrapDiagnostic {
  message: string;
}

/**
 * Closed, secret-free diagnostic catalog. Persisted rows store only the two
 * allowlisted codes; public text is derived here and never accepted from a
 * terminal client or database row.
 */
export const ONBOARDING_BOOTSTRAP_DIAGNOSTICS = {
  BOOTSTRAP_WORKSPACE_INVALID: {
    code: 'BOOTSTRAP_WORKSPACE_INVALID',
    actionCode: 'inspect_workspace',
    message: 'O workspace da atividade não pôde ser validado.',
  },
  BOOTSTRAP_WORKSPACE_LOCKED: {
    code: 'BOOTSTRAP_WORKSPACE_LOCKED',
    actionCode: 'resume_same_workspace',
    message:
      'O workspace está em uso por outra tentativa. Retome no mesmo workspace.',
  },
  BOOTSTRAP_BUNDLE_INVALID: {
    code: 'BOOTSTRAP_BUNDLE_INVALID',
    actionCode: 'generate_new_code',
    message:
      'O pacote de bootstrap não pôde ser validado. Gere um novo código de conexão.',
  },
  BOOTSTRAP_LOCAL_STATE_WRITE_FAILED: {
    code: 'BOOTSTRAP_LOCAL_STATE_WRITE_FAILED',
    actionCode: 'inspect_workspace',
    message:
      'O estado local da conexão não pôde ser gravado. Verifique o workspace.',
  },
  BOOTSTRAP_NO_PROGRESS: {
    code: 'BOOTSTRAP_NO_PROGRESS',
    actionCode: 'resume_same_workspace',
    message:
      'O bootstrap ficou dez minutos sem avançar. Retome no mesmo workspace.',
  },
  BOOTSTRAP_PROTOCOL_MISMATCH: {
    code: 'BOOTSTRAP_PROTOCOL_MISMATCH',
    actionCode: 'generate_new_code',
    message:
      'A versão do bootstrap não é compatível. Gere um novo código de conexão.',
  },
  BOOTSTRAP_CUTOVER_REJECTED: {
    code: 'BOOTSTRAP_CUTOVER_REJECTED',
    actionCode: 'contact_support',
    message:
      'A conclusão da conexão foi rejeitada. Entre em contato com o suporte.',
  },
  BOOTSTRAP_CLIENT_INTERRUPTED: {
    code: 'BOOTSTRAP_CLIENT_INTERRUPTED',
    actionCode: 'resume_same_workspace',
    message:
      'A execução do cliente foi interrompida. Retome no mesmo workspace.',
  },
} as const satisfies Record<
  OnboardingBootstrapFailureCode,
  OnboardingBootstrapDiagnosticDefinition
>;

export type OnboardingProgressStage = Extract<
  OnboardingBootstrapStage,
  'workspace_ready' | 'bundle_ready' | 'local_state_ready'
>;

export type OnboardingFailedStage = Exclude<
  OnboardingBootstrapStage,
  'committed' | 'failed'
>;

export type ReportOnboardingProgressRequest =
  | {
      protocolVersion: 2;
      activityId: string;
      agentId: string;
      stage: OnboardingProgressStage;
      occurredAt: string;
    }
  | ReportOnboardingFailureRequest;

export interface ReportOnboardingFailureRequest {
  protocolVersion: 2;
  activityId: string;
  agentId: string;
  stage: 'failed';
  failedStage: OnboardingFailedStage;
  failureCode: OnboardingBootstrapFailureCode;
  occurredAt: string;
}

export interface ControlPlaneOnboardingAttemptProjection {
  sessionId: string;
  purpose: Exclude<PairingPurpose, 'cleanup-enrollment'>;
  state: OnboardingAttemptState;
  stage: OnboardingBootstrapStage;
  stageChangedAt: string;
  failedAt: string | null;
  diagnostic: ControlPlaneBootstrapDiagnostic | null;
}

/**
 * Domain request used by the resume service. On HTTP the idempotency key is
 * transported exclusively by the `Idempotency-Key` header and the body stays
 * empty, so pairing material can never be reconstructed from a read payload.
 */
export interface ResumeOnboardingAttemptRequest {
  idempotencyKey: OnboardingIdempotencyKey;
}

export const ONBOARDING_CREDENTIAL_STATUSES = [
  'candidate',
  'active',
  'expired',
  'revoked',
  'rotated',
] as const;

export type OnboardingCredentialStatus =
  (typeof ONBOARDING_CREDENTIAL_STATUSES)[number];

export function isOnboardingCredentialStatus(
  value: unknown,
): value is OnboardingCredentialStatus {
  return ONBOARDING_CREDENTIAL_STATUSES.some(status => status === value);
}

export const ONBOARDING_GENERATION_STATUSES = [
  'not-generated',
  'generating',
  'generated',
  'failed',
] as const;

export type OnboardingGenerationStatus =
  (typeof ONBOARDING_GENERATION_STATUSES)[number];

export function isOnboardingGenerationStatus(
  value: unknown,
): value is OnboardingGenerationStatus {
  return ONBOARDING_GENERATION_STATUSES.some(status => status === value);
}

export const ONBOARDING_ROTATION_STATUSES = [
  'not-requested',
  'rotating',
  'rotated',
  'failed',
] as const;

export type OnboardingRotationStatus =
  (typeof ONBOARDING_ROTATION_STATUSES)[number];

export function isOnboardingRotationStatus(
  value: unknown,
): value is OnboardingRotationStatus {
  return ONBOARDING_ROTATION_STATUSES.some(status => status === value);
}

export const ONBOARDING_REVOCATION_STATUSES = [
  'not-requested',
  'revoking',
  'revoked',
  'failed',
] as const;

export type OnboardingRevocationStatus =
  (typeof ONBOARDING_REVOCATION_STATUSES)[number];

export function isOnboardingRevocationStatus(
  value: unknown,
): value is OnboardingRevocationStatus {
  return ONBOARDING_REVOCATION_STATUSES.some(status => status === value);
}

export const ONBOARDING_PAIRING_STATUSES = [
  'created',
  'consumed',
  'expired',
  'blocked',
] as const;

export type OnboardingPairingStatus =
  (typeof ONBOARDING_PAIRING_STATUSES)[number];

export const PAIRING_PURPOSES = [
  'initial',
  'renewal',
  'recovery',
  'cleanup-enrollment',
] as const;

export type PairingPurpose = (typeof PAIRING_PURPOSES)[number];

export function isPairingPurpose(value: unknown): value is PairingPurpose {
  return PAIRING_PURPOSES.some(purpose => purpose === value);
}

export function isOnboardingPairingStatus(
  value: unknown,
): value is OnboardingPairingStatus {
  return ONBOARDING_PAIRING_STATUSES.some(status => status === value);
}

/** Credential lifetime approved for onboarding sessions. */
export const ONBOARDING_CREDENTIAL_TTL_HOURS = 20;

/** Single-use pairing code lifetime approved for phase two. */
export const ONBOARDING_PAIRING_TTL_MINUTES = 10;

/**
 * Current canonical scopes. This closed union includes the historical CLI
 * scope so existing credentials remain representable during migration.
 */
export const AGENT_SCOPES = [
  'cli',
  'events:write',
  'skills:read',
  'approvals:write',
] as const;

export type AgentScope = (typeof AGENT_SCOPES)[number];

export function isAgentScope(value: unknown): value is AgentScope {
  return AGENT_SCOPES.some(scope => scope === value);
}

export const ONBOARDING_CREDENTIAL_KINDS = [
  'legacy-unbound',
  'system',
  /** Operational MCP credential. The only kind AgentAuthorizationService accepts. */
  'onboarding',
  /**
   * Cleanup-only runtime identity created by `cleanup-enrollment` (ADR-016). It
   * exists so a workspace control credential can bind to an agent on an activity
   * whose operational credential is gone (or never existed). It carries no
   * scopes, and AgentAuthorizationService rejects any kind other than
   * 'onboarding', so it can never authorize MCP, events or telemetry.
   */
  'workspace-control',
] as const;

export type OnboardingCredentialKind =
  (typeof ONBOARDING_CREDENTIAL_KINDS)[number];

export function isOnboardingCredentialKind(
  value: unknown,
): value is OnboardingCredentialKind {
  return ONBOARDING_CREDENTIAL_KINDS.some(kind => kind === value);
}

/** An opaque, caller-generated key transported by mutable endpoints. */
export type OnboardingIdempotencyKey = string;

export interface CreateOnboardingSessionRequest {
  repoContext: OnboardingRepoContext;
  idempotencyKey: OnboardingIdempotencyKey;
}

export interface RotateOnboardingSessionRequest {
  idempotencyKey: OnboardingIdempotencyKey;
}

export interface RevokeOnboardingSessionRequest {
  idempotencyKey: OnboardingIdempotencyKey;
  reason: string;
}

export interface CredentialAuthorizationInput {
  agentId: string;
  activityId: string;
  requiredScope: AgentScope;
  now: string;
}

export interface OnboardingSessionSummary {
  id: string;
  activityId: string;
  status: OnboardingSessionStatus;
  expiresAt: string;
  evidenceAcceptedAt: string | null;
  connectedAt: string | null;
  completedAt: string | null;
  secretAvailable: false;
  purpose?: PairingPurpose;
}

export const CONTROL_PLANE_CONNECTION_STATES = [
  'not_started',
  'awaiting_pairing',
  'connecting',
  'connected',
  'expired',
  'revoked',
  'failed',
  'unavailable',
] as const;

export type ControlPlaneConnectionState =
  (typeof CONTROL_PLANE_CONNECTION_STATES)[number];

export const CONTROL_PLANE_EXPIRY_BANDS = [
  'none',
  'two_hours',
  'thirty_minutes',
  'expired',
] as const;

export type ControlPlaneExpiryBand =
  (typeof CONTROL_PLANE_EXPIRY_BANDS)[number];

export const WORKSPACE_CLEANUP_STATES = [
  'not_requested',
  'requested',
  'claimed',
  'retryable_failed',
  'confirmed',
  'overridden',
] as const;

export type WorkspaceCleanupState = (typeof WORKSPACE_CLEANUP_STATES)[number];

export interface ControlPlaneCapability {
  allowed: boolean;
  reasonCode: string | null;
  reason: string | null;
}

export interface ControlPlaneCapabilities {
  pair: ControlPlaneCapability;
  renew: ControlPlaneCapability;
  recover: ControlPlaneCapability;
  /** Exact-owner retry of the latest failed protocol-v2 attempt. */
  resume: ControlPlaneCapability;
  /** Read authorization for the existing Cockpit route, resolved per request. */
  openCockpit: ControlPlaneCapability;
  finalize: ControlPlaneCapability;
  cancel: ControlPlaneCapability;
  selectForDeletion: ControlPlaneCapability;
  overrideCleanup: ControlPlaneCapability;
  /**
   * Owner-only one-time pairing that upgrades a legacy agent so it can finish a
   * pending workspace deletion (ADR-016). Resolved server-side because the UI
   * cannot tell owner from global admin on a terminal activity — and a global
   * admin must never receive pairing material on the owner's behalf.
   */
  enrollCleanup: ControlPlaneCapability;
  /** Owner/global-admin recovery of Claude usage for a completed activity. */
  recoverUsage?: ControlPlaneCapability;
}

export interface ControlPlaneConnectionProjection {
  state: ControlPlaneConnectionState;
  expiryBand: ControlPlaneExpiryBand;
  expiresAt: string | null;
  remainingMs: number | null;
  lastConfirmedAt: string | null;
  sessionId: string | null;
  credentialId: string | null;
}

export interface WorkspaceCleanupProjection {
  state: WorkspaceCleanupState;
  operationId: string | null;
  result: 'deleted' | 'absent' | null;
  lastErrorCode: string | null;
  nextAttemptAt: string | null;
}

export interface ControlPlaneOperationalProjection {
  activityId: string;
  activityCycle: ActivityStatus;
  connection: ControlPlaneConnectionProjection;
  /** Latest protocol-v2 attempt, independent from the operational session. */
  attempt?: ControlPlaneOnboardingAttemptProjection | null;
  workspace: WorkspaceCleanupProjection;
  capabilities: ControlPlaneCapabilities;
  /** Optional only for wire compatibility with projections produced pre-070. */
  finalization?: ControlPlaneFinalizationProjection;
  /** Optional only for wire compatibility with projections produced pre-070. */
  usageRecovery?: ControlPlaneUsageRecoveryProjection;
  /** Sanitized collector health; no session, path or individual usage. */
  usageCollector?: ControlPlaneUsageCollectorProjection;
  /** Aggregate, sanitized session-origin signal health. */
  signalHealth?: import('./session-binding').SessionSignalHealthProjection;
  /** Optional for wire compatibility with projections produced before session observability. */
  sessionObservability?: import('./session-observability').SessionObservabilityProjection;
  observedAt: string;
  stale: boolean;
}

export const CONTROL_PLANE_SKILL_PLAN_STATES = [
  'ready',
  'not_required',
  'unavailable',
] as const;

export type ControlPlaneSkillPlanState =
  (typeof CONTROL_PLANE_SKILL_PLAN_STATES)[number];

/**
 * Human-readable view of the current Skill Vault policy for a Control Plane
 * activity. Technical release identifiers, digests, contents and execution
 * tokens deliberately stay out of this browser projection.
 */
export interface ControlPlaneSkillPlanItem {
  name: string;
  description: string | null;
  canonicalAlias: string | null;
  version: string;
  origin: 'internal' | 'community';
  scopeType: 'corporate' | 'project';
}

export interface ControlPlaneSkillPlanProjection {
  phase: Phase;
  state: ControlPlaneSkillPlanState;
  required: ControlPlaneSkillPlanItem[];
  optionalCount: number;
}

export interface ControlPlaneManagementResponse {
  activity: {
    id: string;
    title: string;
    workPath: ActivityWorkPath;
  };
  projection: ControlPlaneOperationalProjection;
}

export const DELETION_ITEM_OUTCOMES = [
  'deleted',
  'cleanup_pending',
  'rejected_completed',
  'not_found',
  'conflict',
] as const;

export type DeletionItemOutcome = (typeof DELETION_ITEM_OUTCOMES)[number];

export interface ActivityDeletionItemView {
  activityId: string;
  outcome: DeletionItemOutcome;
  cleanupOperationId: string | null;
}

export interface ActivityDeletionBatchView {
  batchId: string;
  items: ActivityDeletionItemView[];
  createdAt: string;
  /**
   * True while at least one item is still `cleanup_pending`. The portal polls
   * with backoff until this is false; a batch with no items left is deleted, so
   * a later read returns 404 rather than an empty tombstone (ADR-015).
   */
  pending: boolean;
}

export interface DeletionBatchRequest {
  activityIds: string[];
}

export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

export type DeletionBatchNormalization =
  | { ids: string[] }
  | { error: 'EMPTY' | 'MALFORMED' | 'TOO_LARGE' };

/**
 * Normalizes an administrative deletion request: deduplicates while preserving
 * first-seen order, rejects any malformed ID outright rather than skipping it,
 * and enforces the configured ceiling AFTER deduplication (ADR-015).
 *
 * Rejecting rather than skipping is deliberate: silently dropping a malformed ID
 * would delete a different set than the operator selected.
 */
export function normalizeDeletionBatchIds(
  value: unknown,
  maxBatchSize: number,
): DeletionBatchNormalization {
  if (!Array.isArray(value)) return { error: 'MALFORMED' };
  if (value.some(entry => !isUuid(entry))) return { error: 'MALFORMED' };
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const entry of value as string[]) {
    const id = entry.toLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  if (ids.length === 0) return { error: 'EMPTY' };
  if (ids.length > maxBatchSize) return { error: 'TOO_LARGE' };
  return { ids };
}

export const WORKSPACE_CLEANUP_REASONS = [
  'completion',
  'cancellation',
  'admin_delete',
] as const;

export type WorkspaceCleanupReason = (typeof WORKSPACE_CLEANUP_REASONS)[number];

export function isWorkspaceCleanupReason(
  value: unknown,
): value is WorkspaceCleanupReason {
  return WORKSPACE_CLEANUP_REASONS.includes(value as WorkspaceCleanupReason);
}

export const WORKSPACE_CLEANUP_RESULTS = ['deleted', 'absent'] as const;

export type WorkspaceCleanupResult = (typeof WORKSPACE_CLEANUP_RESULTS)[number];

export function isWorkspaceCleanupResult(
  value: unknown,
): value is WorkspaceCleanupResult {
  return WORKSPACE_CLEANUP_RESULTS.includes(value as WorkspaceCleanupResult);
}

/**
 * Closed set of sanitized failure codes a local agent may report. An unknown
 * code is rejected at the boundary so free-form output can never reach storage.
 */
export const WORKSPACE_CLEANUP_FAILURE_CODES = [
  'CLEANUP_TARGET_MISMATCH',
  'CLEANUP_TARGET_BUSY',
  'CLEANUP_PERMISSION_DENIED',
  'CLEANUP_IO_ERROR',
  'CLEANUP_PROVIDER_RUNNING',
] as const;

export type WorkspaceCleanupFailureCode =
  (typeof WORKSPACE_CLEANUP_FAILURE_CODES)[number];

export function isWorkspaceCleanupFailureCode(
  value: unknown,
): value is WorkspaceCleanupFailureCode {
  return WORKSPACE_CLEANUP_FAILURE_CODES.includes(
    value as WorkspaceCleanupFailureCode,
  );
}

/** Logical cleanup target. Never a filesystem path. */
export const WORKSPACE_KEY_PATTERN = /^ws_[a-f0-9]{24}$/;

export function isWorkspaceKey(value: unknown): value is string {
  return typeof value === 'string' && WORKSPACE_KEY_PATTERN.test(value);
}

export interface WorkspaceCleanupOperation {
  id: string;
  activityId: string;
  workspaceKey: string;
  reason: WorkspaceCleanupReason;
  state: WorkspaceCleanupState;
  attemptCount: number;
  lastErrorCode: WorkspaceCleanupFailureCode | null;
  nextAttemptAt: string | null;
  leaseExpiresAt: string | null;
  result: WorkspaceCleanupResult | null;
  requestedAt: string;
  confirmedAt: string | null;
}

/**
 * Returned only by a successful claim. `claimToken` is the single reversible
 * value in the contract and is stored centrally as a hash only.
 */
export interface WorkspaceCleanupClaim {
  operationId: string;
  activityId: string;
  workspaceKey: string;
  reason: WorkspaceCleanupReason;
  claimToken: string;
  leaseExpiresAt: string;
  attemptCount: number;
  /**
   * The authoritative target identity, resolved centrally. The local helper
   * compares this whole object against the on-disk marker; without it the helper
   * would only be able to compare the marker with itself.
   */
  workspace: OnboardingWorkspaceSpec;
}

export interface WorkspaceCleanupLeaseView {
  operationId: string;
  leaseExpiresAt: string;
}

export const ACTIVITY_TERMINAL_OUTCOMES = ['completed', 'cancelled'] as const;

export type ActivityTerminalOutcome =
  (typeof ACTIVITY_TERMINAL_OUTCOMES)[number];

export interface ActivityTerminalResult {
  activityId: string;
  outcome: ActivityTerminalOutcome;
  terminalAt: string;
  terminalBy: string;
  currentPhase: string | null;
  cancellationReason: string | null;
  cleanupOperationId: string | null;
  /** True when an earlier identical command already produced this outcome. */
  replayed: boolean;
}

export const CANCELLATION_REASON_MIN_LENGTH = 1;
export const CANCELLATION_REASON_MAX_LENGTH = 2000;

/**
 * Trims first, then bounds. Returns null for anything outside
 * 1..CANCELLATION_REASON_MAX_LENGTH so callers emit a single deterministic
 * error instead of persisting whitespace or an oversized reason.
 */
export function normalizeCancellationReason(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length < CANCELLATION_REASON_MIN_LENGTH) return null;
  if (trimmed.length > CANCELLATION_REASON_MAX_LENGTH) return null;
  return trimmed;
}

/** Reduced projection served to the cleanup-only local credential. */
export interface ControlPlaneLocalStatusResponse {
  activityId: string;
  activityCycle: ActivityStatus;
  connectionState: ControlPlaneConnectionState;
  expiryBand: ControlPlaneExpiryBand;
  expiresAt: string | null;
  workspaceState: WorkspaceCleanupState;
  cleanupAvailable: boolean;
  observedAt: string;
}

export interface WorkspaceCleanupConfig {
  pollIntervalMs: number;
  leaseDurationMs: number;
  retryBaseMs: number;
  retryMaxMs: number;
  maxBatchSize: number;
}

export const DEFAULT_WORKSPACE_CLEANUP_CONFIG: WorkspaceCleanupConfig = {
  pollIntervalMs: 60_000,
  leaseDurationMs: 120_000,
  retryBaseMs: 30_000,
  retryMaxMs: 900_000,
  maxBatchSize: 100,
};

export class WorkspaceCleanupConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkspaceCleanupConfigError';
  }
}

/**
 * Fails closed at startup: an out-of-range or inconsistent value throws rather
 * than being silently merged with the default.
 */
export function validateWorkspaceCleanupConfig(
  input: Partial<WorkspaceCleanupConfig>,
): WorkspaceCleanupConfig {
  const config = {
    ...DEFAULT_WORKSPACE_CLEANUP_CONFIG,
    ...pruneUndefined(input),
  };
  requireIntegerInRange(
    'pollIntervalMs',
    config.pollIntervalMs,
    5_000,
    300_000,
  );
  requireIntegerInRange(
    'leaseDurationMs',
    config.leaseDurationMs,
    1,
    Number.MAX_SAFE_INTEGER,
  );
  requireIntegerInRange(
    'retryBaseMs',
    config.retryBaseMs,
    1,
    Number.MAX_SAFE_INTEGER,
  );
  requireIntegerInRange(
    'retryMaxMs',
    config.retryMaxMs,
    1,
    Number.MAX_SAFE_INTEGER,
  );
  requireIntegerInRange('maxBatchSize', config.maxBatchSize, 1, 500);
  // At least twice the poll interval, so a supervisor always gets two renewal
  // opportunities before the lease expires. Read as ">=" deliberately: the
  // documented defaults (poll 60s, lease 120s) are exactly 2x, and a strict ">"
  // would make the spec's own defaults invalid.
  if (config.leaseDurationMs < 2 * config.pollIntervalMs) {
    throw new WorkspaceCleanupConfigError(
      'spassuCode.onboarding.cleanup.leaseDurationMs must be at least twice pollIntervalMs.',
    );
  }
  if (config.retryMaxMs < config.retryBaseMs) {
    throw new WorkspaceCleanupConfigError(
      'spassuCode.onboarding.cleanup.retryMaxMs must be greater than or equal to retryBaseMs.',
    );
  }
  return config;
}

function pruneUndefined(
  input: Partial<WorkspaceCleanupConfig>,
): Partial<WorkspaceCleanupConfig> {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as Partial<WorkspaceCleanupConfig>;
}

function requireIntegerInRange(
  key: keyof WorkspaceCleanupConfig,
  value: number,
  min: number,
  max: number,
): void {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new WorkspaceCleanupConfigError(
      `spassuCode.onboarding.cleanup.${key} must be an integer between ${min} and ${max}.`,
    );
  }
}

/**
 * Exponential backoff from `retryBaseMs`, doubling per recorded attempt and
 * clamped at `retryMaxMs`. `attemptCount` is the number of attempts already
 * made, so the first retry uses the base delay exactly.
 */
export function nextCleanupAttemptDelayMs(
  attemptCount: number,
  config: Pick<WorkspaceCleanupConfig, 'retryBaseMs' | 'retryMaxMs'>,
): number {
  const attempts = Number.isFinite(attemptCount)
    ? Math.max(1, Math.trunc(attemptCount))
    : 1;
  const exponent = Math.min(attempts - 1, 32);
  const delay = config.retryBaseMs * 2 ** exponent;
  return Math.min(delay, config.retryMaxMs);
}

export const CONTROL_PLANE_HANDOFF_ERROR_CODES = [
  'CONTROL_PLANE_EXECUTION_MODE_CONFLICT',
  'CONTROL_PLANE_SECURE_LIFECYCLE_REQUIRED',
] as const;

export type ControlPlaneHandoffErrorCode =
  (typeof CONTROL_PLANE_HANDOFF_ERROR_CODES)[number];

export const CONTROL_PLANE_HANDOFF_INGEST_OUTCOMES = [
  'transitioned',
  'idempotent',
  'not-eligible',
  'not-applicable',
] as const;

export type ControlPlaneHandoffIngestOutcome =
  (typeof CONTROL_PLANE_HANDOFF_INGEST_OUTCOMES)[number];

export interface ControlPlaneHandoffIngestResult {
  outcome: ControlPlaneHandoffIngestOutcome;
  state: ActivityHandoffState;
  sessionStatus: OnboardingSessionStatus | null;
}

export interface AgentEventsIngestResponse {
  ingested: number;
  handoff: ControlPlaneHandoffIngestResult;
  usageRecords?: AgentUsageIngestResult[];
}

export interface AgentUsageIngestResult {
  usageId: string;
  usageKey: string;
  created: boolean;
  nature: 'reported' | 'calculated';
  outcome: 'created' | 'replayed' | 'replaced';
}

export const ACTIVITY_HANDOFF_ALERT_CODES = [
  'HANDOFF_CONNECTION_STALLED',
] as const;

export type ActivityHandoffAlertCode =
  (typeof ACTIVITY_HANDOFF_ALERT_CODES)[number];

export interface ActivityHandoffProjection {
  state: ActivityHandoffState;
  sessionStatus: OnboardingSessionStatus | null;
  evidenceAcceptedAt: string | null;
  connectedAt: string | null;
  completedAt: string | null;
  alert: {
    code: ActivityHandoffAlertCode;
    since: string;
  } | null;
}

export interface ControlPlaneOnboardingStateResponse {
  activity: {
    id: string;
    title: string;
    workPath: ActivityWorkPath;
    handoff?: ActivityHandoffProjection;
  };
  connectPath: OnboardingConnectPath;
  session: OnboardingSessionSummary | null;
  pairing: OnboardingPairingSummary | null;
}

/** Returned only by a successful first emission or explicit rotation. */
export interface OnboardingSessionCreatedResponse {
  id: string;
  activityId: string;
  status: OnboardingSessionStatus;
  expiresAt: string;
  secretAvailable: true;
  agentKey: string;
  shellScript: string;
}

export type OnboardingSessionResponse =
  | OnboardingSessionCreatedResponse
  | OnboardingSessionSummary;

export interface CreateOnboardingPairingRequest {
  /** @deprecated New pairings always clone; accepted temporarily for rollout. */
  repoContext?: OnboardingRepoContext;
  idempotencyKey: OnboardingIdempotencyKey;
  purpose?: PairingPurpose;
}

/** Structured local workspace instructions for the terminal pairing client. */
export interface OnboardingWorkspaceSpec {
  version: 1;
  activityId: string;
  workspaceKey: string;
  provider: import('./scm').ProviderId;
  repoOwner: string;
  repoProject: string | null;
  repoName: string;
  cloneUrl: string;
}

export interface OnboardingPairingSummary {
  id: string;
  activityId: string;
  status: OnboardingPairingStatus;
  expiresAt: string;
  createdAt: string;
  codeAvailable: false;
  purpose?: PairingPurpose;
}

/** A pairing code is single-use coordination data, never an Agent key. */
export interface OnboardingPairingCreatedResponse {
  id: string;
  activityId: string;
  status: OnboardingPairingStatus;
  expiresAt: string;
  createdAt: string;
  codeAvailable: true;
  pairingCode: string;
  terminalCommand: string;
  purpose?: PairingPurpose;
}

export type ResumeOnboardingAttemptResponse =
  | OnboardingPairingCreatedResponse
  | OnboardingPairingSummary;

export interface RedeemOnboardingPairingRequest {
  pairingCode: string;
  activityId: string;
  agentId: string;
  /**
   * Hash of the operational (MCP) credential.
   *
   * Absent for `cleanup-enrollment`, and only for it: that purpose installs the
   * cleanup-only identity on an already terminal activity, so handing back an
   * operational credential would re-grant MCP access to something that has ended
   * (ADR-016). Every other purpose requires it.
   */
  credentialHash?: string;
  /** Hash of the cleanup-only credential generated and retained locally. */
  controlCredentialHash: string;
  /**
   * Hash of the telemetry-only credential retained locally. Optional for
   * compatibility and omitted by cleanup-only enrollment.
   */
  telemetryCredentialHash?: string;
}

/** Terminal-only response. It never contains the reversible Agent key. */
export interface OnboardingPairingRedeemedResponse {
  session: OnboardingSessionSummary;
  pairingId: string;
  purpose: PairingPurpose;
  repoContext: OnboardingRepoContext;
  /** Absent only for pairings issued before structured workspaces existed. */
  workspace?: OnboardingWorkspaceSpec;
  connectPath: OnboardingConnectPath;
  bundle: OnboardingBootstrapBundle;
  shellScript: string;
  validationUrl: string;
  /**
   * Absent for `cleanup-enrollment`: that purpose registers no operational
   * candidate, and the generic cutover requires an active activity plus an
   * onboarding candidate — both of which it deliberately lacks (ADR-016).
   * Advertising a cutover the client can never complete left the enrollment
   * stranded halfway.
   */
  cutoverUrl?: string;
  abandonUrl: string;
}

export interface ConfirmOnboardingPairingCutoverRequest {
  protocolVersion: 2;
  activityId: string;
  agentId: string;
  bundleVersion: string;
  expectedStage: 'local_state_ready';
  workspace: OnboardingWorkspaceSpec;
  report: {
    workspacePrepared: true;
    bundleValidated: true;
    localStatePersisted: true;
  };
}

export interface ConfirmOnboardingPairingCutoverResponse {
  pairingId: string;
  session: OnboardingSessionSummary;
  promoted: boolean;
}

export const ONBOARDING_PUBLIC_ERROR_CODES = [
  'ONBOARDING_INVALID_REQUEST',
  'ONBOARDING_FORBIDDEN',
  'ONBOARDING_NOT_FOUND',
  'ONBOARDING_IDEMPOTENCY_KEY_REQUIRED',
  'ONBOARDING_IDEMPOTENCY_CONFLICT',
  'ONBOARDING_ACTIVE_SESSION_CONFLICT',
  'ONBOARDING_SECRET_UNAVAILABLE',
  'ONBOARDING_CREDENTIAL_EXPIRED',
  'ONBOARDING_CREDENTIAL_REVOKED',
  'ONBOARDING_SCOPE_DENIED',
  'ONBOARDING_ACTIVITY_MISMATCH',
  'ONBOARDING_PAIRING_INVALID',
  'ONBOARDING_PAIRING_EXPIRED',
  'ONBOARDING_PAIRING_BLOCKED',
  'ONBOARDING_PAIRING_CONSUMED',
  'ONBOARDING_LEGACY_ROUTE_RETIRED',
  'ONBOARDING_CONNECT_DISCONTINUED',
  'ONBOARDING_BOOTSTRAP_REPORT_INVALID',
  'ONBOARDING_BOOTSTRAP_PROTOCOL_REQUIRED',
  'ONBOARDING_BOOTSTRAP_STAGE_INVALID',
  'ONBOARDING_BOOTSTRAP_STALE_ATTEMPT',
  'ONBOARDING_RESUME_NOT_AVAILABLE',
  'CONTROL_CREDENTIAL_INVALID',
  'CONTROL_CREDENTIAL_REVOKED',
  'CLEANUP_NOT_AVAILABLE',
  'CLEANUP_CLAIM_CONFLICT',
  'CLEANUP_LEASE_EXPIRED',
  'CLEANUP_TARGET_MISMATCH',
  'ACTIVITY_TERMINAL',
  'ACTIVITY_NOT_FINALIZABLE',
  'ACTIVITY_COMPLETED_DELETE_FORBIDDEN',
  'GLOBAL_ADMIN_REQUIRED',
  'INVALID_CANCELLATION_REASON',
  'ONBOARDING_INTERNAL_ERROR',
] as const;

export type OnboardingPublicErrorCode =
  (typeof ONBOARDING_PUBLIC_ERROR_CODES)[number];

export interface OnboardingPublicError {
  code: OnboardingPublicErrorCode;
  message: string;
  action?: string;
}

/** Existing bootstrap shapes kept additive until the dedicated UI migrates. */
export interface OnboardingSession {
  id: string;
  activityId: string;
  userId: string;
  repoContext: OnboardingRepoContext;
  connectPath: OnboardingConnectPath;
  agentId: string | null;
  bundleVersion: string;
  status: OnboardingSessionStatus;
  firstTelemetryAt: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingBootstrapEnvVar {
  name: string;
  value: string;
}

export interface OnboardingBootstrapBundle {
  version: string;
  canonicalPath: string;
  runtimeTargets: string[];
  downloadUrl: string | null;
}

export interface OnboardingBundleFile {
  path: string;
  content: string;
  sha256: string;
}

export interface OnboardingBundleAsset extends OnboardingBundleFile {
  executable: boolean;
}

export interface OnboardingBundleManifest {
  version: string;
  hookVersion: string;
  supervisorVersion: string;
  usageCollectorVersion: string;
  generatedAt: string;
  canonicalPath: string;
  runtimeTargets: string[];
  files: OnboardingBundleFile[];
  assets: OnboardingBundleAsset[];
}

/** @deprecated Use OnboardingSessionResponse for the new Control Plane API. */
export interface OnboardingBootstrapResponse {
  sessionId: string;
  activityId: string;
  repoContext: OnboardingRepoContext;
  connectPath: OnboardingConnectPath;
  status: OnboardingSessionStatus;
  envExports: OnboardingBootstrapEnvVar[];
  bundle: OnboardingBootstrapBundle;
  shellScript: string;
  validationUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingValidationChecks {
  bundleMaterialized: boolean;
  runtimeProjectionReady: boolean;
  remoteReachable: boolean;
  firstTelemetryConfirmed: boolean;
}

export interface OnboardingValidationStatus {
  sessionId: string;
  status: 'in_progress' | 'failed' | 'succeeded';
  checks: OnboardingValidationChecks;
  failureReason: string | null;
  firstTelemetryAt: string | null;
  checkedAt: string;
}

export const TERMINAL_WORK_PATHS = [
  'control-plane',
  'cli-standalone',
] as const satisfies readonly ActivityWorkPath[];

/** Returns whether the activity follows one of the terminal work paths. */
export function isTerminalWorkPath(
  workPath: ActivityWorkPath | null | undefined,
): boolean {
  return workPath === 'control-plane' || workPath === 'cli-standalone';
}

/** Missing legacy values retain the canonical `frontier-web` default. */
export function isFrontierWorkPath(
  workPath: ActivityWorkPath | null | undefined,
): boolean {
  return (
    workPath === null || workPath === undefined || workPath === 'frontier-web'
  );
}

/** Derives the onboarding route for terminal paths; Frontier has no onboarding. */
export function deriveConnectPath(
  workPath: ActivityWorkPath,
): OnboardingConnectPath | null {
  if (workPath === 'control-plane') return 'control-plane-enriched';
  if (workPath === 'cli-standalone') return 'cli-standalone';
  return null;
}

export type ActivityHandoffState =
  | 'not-applicable'
  | 'awaiting-local-onboarding'
  | 'onboarding-in-progress'
  | 'connected'
  | 'handoff-failed';

export type ObservabilitySource = 'worker' | 'agent';

export type ObservabilityProvenance =
  | 'worker-sdk'
  | 'cli-sync'
  | 'agent-event'
  | 'hook-passive'
  | 'mcp-report'
  | 'transcript-parse'
  | 'manual';

export type ObservabilityFidelity = 'full' | 'partial' | 'low';

export type SkillOrigin =
  | 'spassu'
  | 'provider-native'
  | 'user-custom'
  | 'community'
  | 'unknown';

export type SkillRegistrationType = 'cataloged' | 'declared' | 'inferred';

export type SkillInvocationState =
  | 'em_andamento'
  | 'concluida'
  | 'falha'
  | 'cancelada'
  | 'incompleta';

export type SkillInvocationIdentification =
  | 'confirmada'
  | 'declarada'
  | 'inferida';

export type SkillInvocationSource =
  | 'web'
  | 'control-plane-enriched'
  | 'cli-standalone';

export interface SkillInvocationTaskEvidence {
  taskNumber: string;
  state: 'concluida' | 'falha' | 'em_andamento';
  startedAt: string | null;
  endedAt: string | null;
  eventIds: string[];
}

export interface CanonicalTimelineEvent {
  id: string;
  activityId: string;
  activitySource: ActivitySource;
  source: ObservabilitySource;
  eventType: string;
  eventFamily: string;
  content: string;
  agentId: string | null;
  phase: Phase | null;
  provenance: ObservabilityProvenance;
  fidelity: ObservabilityFidelity;
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface SkillInvocationProjection {
  invocationId: string;
  activityId: string;
  activitySource: ActivitySource;
  invocationSource: SkillInvocationSource;
  skillName: string;
  skillOrigin: SkillOrigin;
  identification: SkillInvocationIdentification;
  registrationType: SkillRegistrationType;
  state: SkillInvocationState;
  source: ObservabilitySource;
  provenance: ObservabilityProvenance;
  fidelity: ObservabilityFidelity;
  timestamp: string;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
  taskNumber: string | null;
  parentInvocationId: string | null;
  attempt: number;
  supersedesInvocationId: string | null;
  taskEvidence: SkillInvocationTaskEvidence[];
  evidenceEventIds: string[];
  correlationKey: string;
  nativeInvocationKey?: string | null;
  tokens: number | null;
  cost: number | null;
  artifactIds: string[];
  warnings: string[];
  agentId?: string | null;
  provider?: string;
  model?: string;
  agentType?: string;
  phase?: Phase | null;
  metadata: Record<string, unknown>;
}

export type ActivityManagementNextActionKind =
  | 'resolve-blocker'
  | 'terminal-approval'
  | 'terminal-close'
  | 'workflow';

export interface ActivityManagementNextAction {
  kind: ActivityManagementNextActionKind;
  label: string;
  description: string;
  evidenceLabel: string | null;
  evidenceEventIds: string[];
  createsPortalAction: boolean;
}

export interface ActivityManagementTelemetryWarning {
  label: string;
  description: string;
  lastEventAt: string | null;
  silentForMinutes: number | null;
}

export type ActivityManagementSessionCoverageState =
  | 'observed'
  | 'uncovered'
  | 'stalled';

export type ActivityManagementSessionEmitterHealth =
  | 'current'
  | 'missing'
  | 'outdated'
  | 'invalid';

export type ActivityManagementSessionPairingHealth =
  | 'paired'
  | 'partial'
  | 'orphan-only'
  | 'not-observed';

export interface ActivityManagementSessionCoverageGap {
  droppedTotal: number;
  firstDroppedAt: string | null;
  lastDroppedAt: string | null;
}

/**
 * Leitura gerencial compacta da cobertura passiva da sessão corrente.
 * É nula fora do workPath control-plane, onde E1 não promete cobertura.
 */
export interface ActivityManagementSessionCoverage {
  state: ActivityManagementSessionCoverageState;
  label: string;
  description: string;
  telemetrySessionId: string | null;
  emitterHealth: ActivityManagementSessionEmitterHealth;
  pairingHealth: ActivityManagementSessionPairingHealth;
  emitterVersion: string | null;
  lastSignalAt: string | null;
  lastIngestedAt: string | null;
  stoppedAt: string | null;
  silentForMinutes: number | null;
  gap: ActivityManagementSessionCoverageGap | null;
  qualifiers: string[];
}

export type ActivityJourneyStepState =
  | 'completed'
  | 'in-progress'
  | 'failed'
  | 'blocked'
  | 'uncertain'
  | 'not-observed';

export type ActivityJourneyStepKind = 'canonical' | 'checkpoint';

export interface ActivityJourneyStep {
  id: string;
  kind: ActivityJourneyStepKind;
  label: string;
  state: ActivityJourneyStepState;
  phase: Phase | null;
  timestamp: string | null;
  completedAt: string | null;
  evidenceEventIds: string[];
  invocationIds: string[];
  confidence: 'confirmed' | 'calculated' | 'estimated' | 'unavailable';
  retryAttempt: number | null;
  supersedesInvocationId: string | null;
  gapAfterPrevious: boolean;
  explanation: string;
}

export interface ActivityManagementSituation {
  statusLabel: string;
  currentStepLabel: string;
  healthLabel: string;
  responsibleLabel: string;
  agentLabel: string;
  updatedAt: string | null;
  partialData: boolean;
}

export interface ActivityManagementJourney {
  observedWindow: {
    startsAt: string | null;
    endsAt: string | null;
  };
  steps: ActivityJourneyStep[];
}

export interface ActivityManagementSkillRow {
  invocationId: string;
  skillName: string;
  origin: SkillOrigin;
  originLabel: string;
  status: SkillInvocationState;
  statusLabel: string;
  durationMs: number | null;
  durationLabel: string;
  identification: SkillInvocationIdentification;
  identificationLabel: string | null;
  attempt: number;
  supersedesInvocationId: string | null;
  evidenceEventIds: string[];
}

export interface ActivityManagementStepDetail {
  stepId: string;
  stepLabel: string;
  narrative: string;
  summaryFacts: string[];
  completedWork: string[];
  validationNotes: string[];
  attentionItems: string[];
  artifactLabels: string[];
  commitLabels: string[];
  technicalEventIds: string[];
  skills: ActivityManagementSkillRow[];
}

export interface ActivityManagementTimelineGroup {
  stepId: string;
  stepLabel: string;
  kind: ActivityJourneyStepKind | 'outside-step';
  total: number;
  markers: ManagementTimelineMarker[];
  emptyState: string | null;
}

export interface ActivityManagementSummaryPage {
  activityId: string;
  generatedAt: string;
  workPath: ActivityWorkPath;
  situation: ActivityManagementSituation;
  nextAction: ActivityManagementNextAction;
  telemetryWarning: ActivityManagementTelemetryWarning | null;
  sessionCoverage: ActivityManagementSessionCoverage | null;
  journey: ActivityManagementJourney;
  stepDetails: ActivityManagementStepDetail[];
  timelineGroups: ActivityManagementTimelineGroup[];
}

export type TraceArtifactCategory =
  | 'main-document'
  | 'task-file'
  | 'report'
  | 'evidence'
  | 'commit-pr'
  | 'context'
  | 'working-document'
  | 'detected';

export type TraceArtifactStatus =
  | 'approved'
  | 'pending'
  | 'published'
  | 'detected'
  | 'missing';

export type TraceArtifactConfidence =
  | 'confirmed'
  | 'correlated'
  | 'detected'
  | 'unavailable';

export interface TraceArtifactParticipantRef {
  id: string;
  label: string;
  type: 'user' | 'agent' | 'system' | 'unknown';
  provider: string | null;
}

export interface TraceArtifactItem {
  id: string;
  activityId: string;
  category: TraceArtifactCategory;
  categoryLabel: string;
  name: string;
  artifactType: string;
  status: TraceArtifactStatus;
  statusLabel: string;
  summary?: string | null;
  phase: Phase | null;
  taskNumber: string | null;
  createdAt: string | null;
  version: number;
  revisionKey: string;
  path: string | null;
  origin: string;
  confidence: TraceArtifactConfidence;
  sourceEventId: string | null;
  invocationId: string | null;
  skillName: string | null;
  participant: TraceArtifactParticipantRef | null;
  documentKey?: string | null;
  contentHash?: string | null;
  sizeBytes?: number | null;
  mtime?: string | null;
  observedAt?: string | null;
  observationSequence?: number | null;
}

export type TraceDeliveryKind =
  | 'prd'
  | 'techspec'
  | 'tasks'
  | 'qa-report'
  | 'pull-request'
  | 'context-support'
  | 'other-evidence';

export interface TraceDeliveryCard {
  kind: Exclude<TraceDeliveryKind, 'other-evidence'>;
  label: string;
  status: TraceArtifactStatus;
  statusLabel: string;
  summary: string;
  quality?: TraceDeliveryQuality | null;
  latestArtifact: TraceArtifactItem | null;
  history: TraceArtifactItem[];
  contextCount: number;
  contextReductionPercent: number | null;
  displayMode: 'card' | 'grouped-row';
  readable: boolean;
  emptyState: TraceArtifactEmptyState | null;
}

export interface TraceOtherEvidenceRow {
  kind: 'other-evidence';
  label: 'Outras evidências';
  displayMode: 'summary-row';
  coverage: 'observed' | 'partial' | 'unavailable';
  detectedCount: number | null;
  publishedCount: number | null;
  items: TraceArtifactItem[];
  reasonCode?: string | null;
}

export type TraceDeliveryEntry = TraceDeliveryCard | TraceOtherEvidenceRow;

export interface TraceDeliveryQuality {
  state: TraceQualityState;
  stateLabel: string;
  explanation: string;
}

export interface TraceArtifactContentPage {
  activityId: string;
  artifactId: string;
  name: string;
  statusLabel: string;
  version: number;
  content: string;
  contentPage: {
    offset: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
  metadata: {
    category: TraceArtifactCategory;
    artifactType: string;
    phase: Phase | null;
    path: string | null;
    createdAt: string | null;
  };
}

export type TraceQualityState =
  | 'passed'
  | 'failed'
  | 'partial'
  | 'warning'
  | 'not-executed'
  | 'not-applicable'
  | 'no-evidence'
  | 'pending-approval';

export type TraceQualityProvenance =
  | 'human'
  | 'deterministic'
  | 'ai'
  | 'unknown';

export interface TraceQualityEvidence {
  id: string;
  label: string;
  source: 'artifact' | 'event' | 'approval' | 'activity';
  timestamp: string | null;
  artifactId: string | null;
  eventId: string | null;
  approvalId: string | null;
}

export interface TraceQualityItem {
  id: string;
  scope: 'step' | 'skill' | 'artifact';
  deliveryKind?: TraceDeliveryKind | null;
  scopeLabel: string;
  state: TraceQualityState;
  stateLabel: string;
  provenance: TraceQualityProvenance;
  provenanceLabel: string;
  explanation: string;
  evidence: TraceQualityEvidence[];
}

export interface TraceArtifactCategorySummary {
  category: TraceArtifactCategory;
  label: string;
  total: number;
  statuses: Partial<Record<TraceArtifactStatus, number>>;
}

export interface TraceArtifactEmptyState {
  status: 'missing';
  label: string;
  explanation: string;
}

export interface TraceArtifactCollection {
  activityId: string;
  generatedAt: string;
  total: number;
  artifacts: TraceArtifactItem[];
  deliveryCards: TraceDeliveryEntry[];
  qualitySummary: TraceQualityItem[];
  categories: TraceArtifactCategorySummary[];
  emptyState: TraceArtifactEmptyState | null;
}

export type TraceParticipantRole = 'responsible' | 'collaborator';
export type TraceParticipantKind = 'person' | 'agent' | 'system';
export type TraceParticipantContributionKind =
  | 'activity'
  | 'event'
  | 'artifact'
  | 'skill-invocation';

export interface TraceParticipantPeriod {
  startedAt: string;
  endedAt: string | null;
}

export interface TraceParticipant {
  id: string;
  label: string;
  role: TraceParticipantRole;
  kind: TraceParticipantKind;
  userId: string | null;
  agentId: string | null;
  agentType: string | null;
  provider: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  periods: TraceParticipantPeriod[];
  contributionKinds: TraceParticipantContributionKind[];
  skillNames: string[];
  phases: Phase[];
  confidence: 'confirmed' | 'correlated';
}

export interface TraceParticipantsAbsenceState {
  status: 'available' | 'unavailable';
  label: string;
  explanation: string;
}

export interface TraceParticipantsPage {
  activityId: string;
  generatedAt: string;
  total: number;
  participants: TraceParticipant[];
  collaboratorState: TraceParticipantsAbsenceState;
}

export type TraceDoraIndicatorKey =
  | 'changeLeadTime'
  | 'deploymentFrequency'
  | 'changeFailureRate'
  | 'failedDeploymentRecoveryTime'
  | 'deploymentReworkRate';

export type TraceDoraIndicatorState =
  | 'calculated'
  | 'unavailable'
  | 'not-applicable';

export interface TraceDoraEvidence {
  id: string;
  type: 'pull-request' | 'commit' | 'activity' | 'event';
  label: string;
  url: string | null;
  timestamp: string | null;
  sourceEventId: string | null;
}

export interface TraceDoraIndicator {
  key: TraceDoraIndicatorKey;
  label: string;
  state: TraceDoraIndicatorState;
  value: string | null;
  unit: string | null;
  definition: string;
  period: string;
  evidence: TraceDoraEvidence[];
  limitations: string[];
  updatedAt: string | null;
}

export interface TraceDoraActivityPage {
  activityId: string;
  generatedAt: string;
  summaryState: {
    status: 'available' | 'unavailable';
    label: string;
    explanation: string;
  };
  indicators: TraceDoraIndicator[];
}

export type TraceAuditItemCategory =
  | 'skill'
  | 'sync'
  | 'runtime'
  | 'health'
  | 'event'
  | 'session';

export interface TraceAuditItem {
  id: string;
  activityId: string;
  category: TraceAuditItemCategory;
  title: string;
  summary: string;
  timestamp: string;
  state: string;
  source: ObservabilitySource | 'system';
  provenance: ObservabilityProvenance | 'derived';
  fidelity: ObservabilityFidelity | 'derived';
  origin: string;
  confidence: string;
  invocationId: string | null;
  correlationId: string | null;
  sourceEventIds: string[];
  attempt: number | null;
  supersedesInvocationId: string | null;
  metadata: Record<string, unknown>;
}

export interface TraceAuditTrailPage {
  activityId: string;
  generatedAt: string;
  total: number;
  items: TraceAuditItem[];
  summaryState: {
    status: 'available' | 'unavailable';
    label: string;
    explanation: string;
  };
}

/**
 * Frontend consumers may assume only the required fields below.
 * Optional fields must degrade to explicit "unknown"/hidden states instead of zero-like defaults.
 */
export interface ObservabilityBoardProjection {
  activityId: string;
  activitySource: ActivitySource;
  executionMode: ActivityExecutionMode;
  currentPhase: Phase | null;
  generatedAt: string;
  timeline?: CanonicalTimelineEvent[];
  skillInvocations?: SkillInvocationProjection[];
  currentCheckpoint?: string | null;
  totalCost?: number | null;
  totalTokens?: number | null;
  primaryModel?: string | null;
  healthState?: string | null;
  branch?: string | null;
  prUrl?: string | null;
}

export type PhaseTransitionTrigger =
  | 'manual_approval'
  | 'agent_approval'
  | 'execution_complete';

export interface ApprovalPolicy {
  mode: 'manual' | 'agent' | 'hybrid' | 'none';
  requiresHumanApproval: boolean;
  agentToolEnabled: boolean;
}

export interface PhaseActionPolicy {
  canStart: boolean;
  canApprove: boolean;
  canExecuteTasks: boolean;
  canPauseResumeExecution: boolean;
  canRetryTask: boolean;
}

export type AutoStartPolicy = 'none' | 'backend' | 'frontend';

export type PhaseSessionLifecycle =
  | 'pauses_live'
  | 'ends_after_run'
  | 'no_agent_session';

export type PhaseConformityRecoveryMode =
  | 'revalidate_artifact'
  | 'rerun_phase'
  | 'revalidate_delivery'
  // `reset_pr` nunca é política de fase: é um recoveryAction derivado apenas no
  // summary quando a fase `pronto` está em `PR_INCONSISTENT` (pr_url de outro
  // repositório). Habilita a ação humana "Refazer PR" (limpa a pr_url) em vez da
  // reconciliação de `revalidate_delivery`, que falharia para esse estado.
  | 'reset_pr'
  | 'replan_tasks'
  | 'none';

export interface PhaseConformityPolicy {
  sessionLifecycle: PhaseSessionLifecycle;
  recoveryMode: PhaseConformityRecoveryMode;
}

export interface PipelinePhaseDefinition {
  id: Phase;
  label: string;
  skill: string | null;
  artifactType: string | null;
  approvalPolicy: ApprovalPolicy;
  autoStart: boolean;
  autoStartPolicy: AutoStartPolicy;
  sessionLifecycle: PhaseSessionLifecycle;
  conformityRecoveryMode: PhaseConformityRecoveryMode;
  actions: PhaseActionPolicy;
}

export interface PhaseTransitionRule {
  from: Phase;
  to: Phase;
  trigger: PhaseTransitionTrigger;
  autoTransition: boolean;
}

export interface PipelineDefinition {
  version: string;
  phases: PipelinePhaseDefinition[];
  transitionRules: PhaseTransitionRule[];
  terminalPhases: Phase[];
}

export const PHASES_ORDER: Phase[] = [
  'gerando-prd',
  'gerando-techspec',
  'dividindo-tasks',
  'executando',
  'qa',
  'pronto',
  'finalizado',
];

const PHASE_SET = new Set<Phase>(PHASES_ORDER);
const TASK_NUMBER_PATTERN = /^\d+(?:\.\d+)*$/;

export const DEFAULT_PIPELINE_DEFINITION: PipelineDefinition = {
  version: '2026-07-16',
  phases: [
    {
      id: 'gerando-prd',
      label: 'PRD',
      skill: '/criar-prd',
      artifactType: 'prd',
      approvalPolicy: {
        mode: 'manual',
        requiresHumanApproval: true,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'pauses_live',
      conformityRecoveryMode: 'revalidate_artifact',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'gerando-techspec',
      label: 'TechSpec',
      skill: '/criar-techspec',
      artifactType: 'techspec',
      approvalPolicy: {
        mode: 'manual',
        requiresHumanApproval: true,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'pauses_live',
      conformityRecoveryMode: 'revalidate_artifact',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'dividindo-tasks',
      label: 'Tasks',
      skill: '/criar-tasks',
      artifactType: 'tasks',
      approvalPolicy: {
        mode: 'manual',
        requiresHumanApproval: true,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'pauses_live',
      conformityRecoveryMode: 'revalidate_artifact',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'executando',
      label: 'Código',
      skill: '/executar-task',
      artifactType: null,
      approvalPolicy: {
        mode: 'none',
        requiresHumanApproval: false,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'no_agent_session',
      conformityRecoveryMode: 'replan_tasks',
      actions: {
        canStart: false,
        canApprove: false,
        canExecuteTasks: true,
        canPauseResumeExecution: true,
        canRetryTask: true,
      },
    },
    {
      id: 'qa',
      label: 'Testes',
      skill: '/executar-qa',
      artifactType: 'test-report',
      approvalPolicy: {
        mode: 'agent',
        requiresHumanApproval: false,
        agentToolEnabled: true,
      },
      autoStart: true,
      autoStartPolicy: 'backend',
      sessionLifecycle: 'ends_after_run',
      conformityRecoveryMode: 'rerun_phase',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'pronto',
      label: 'PR',
      skill: '/criar-pr',
      artifactType: null,
      approvalPolicy: {
        mode: 'hybrid',
        requiresHumanApproval: true,
        agentToolEnabled: true,
      },
      autoStart: true,
      autoStartPolicy: 'frontend',
      sessionLifecycle: 'ends_after_run',
      conformityRecoveryMode: 'revalidate_delivery',
      actions: {
        canStart: true,
        canApprove: true,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
    {
      id: 'finalizado',
      label: 'Finalizado',
      skill: null,
      artifactType: null,
      approvalPolicy: {
        mode: 'none',
        requiresHumanApproval: false,
        agentToolEnabled: false,
      },
      autoStart: false,
      autoStartPolicy: 'none',
      sessionLifecycle: 'no_agent_session',
      conformityRecoveryMode: 'none',
      actions: {
        canStart: false,
        canApprove: false,
        canExecuteTasks: false,
        canPauseResumeExecution: false,
        canRetryTask: false,
      },
    },
  ],
  transitionRules: [
    {
      from: 'gerando-prd',
      to: 'gerando-techspec',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'gerando-techspec',
      to: 'dividindo-tasks',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'dividindo-tasks',
      to: 'executando',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'executando',
      to: 'qa',
      trigger: 'execution_complete',
      autoTransition: true,
    },
    {
      from: 'executando',
      to: 'qa',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'qa',
      to: 'pronto',
      trigger: 'agent_approval',
      autoTransition: true,
    },
    {
      from: 'qa',
      to: 'pronto',
      trigger: 'manual_approval',
      autoTransition: false,
    },
    {
      from: 'pronto',
      to: 'finalizado',
      trigger: 'manual_approval',
      autoTransition: false,
    },
  ],
  terminalPhases: ['finalizado'],
};

export function clonePipelineDefinition(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PipelineDefinition {
  return {
    ...definition,
    phases: definition.phases.map(phase => ({
      ...phase,
      approvalPolicy: { ...phase.approvalPolicy },
      actions: { ...phase.actions },
    })),
    transitionRules: definition.transitionRules.map(rule => ({ ...rule })),
    terminalPhases: [...definition.terminalPhases],
  };
}

export function getDefaultPipelineDefinition(): PipelineDefinition {
  return clonePipelineDefinition(DEFAULT_PIPELINE_DEFINITION);
}

export function isValidPhase(phase: string): phase is Phase {
  return PHASE_SET.has(phase as Phase);
}

export function getPhaseOrder(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Phase[] {
  return definition.phases.map(phase => phase.id);
}

export function getPhaseDefinition(
  phase: Phase,
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PipelinePhaseDefinition | undefined {
  return definition.phases.find(item => item.id === phase);
}

export function getPhaseConformityPolicy(
  phase: Phase,
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PhaseConformityPolicy | undefined {
  const phaseDefinition = getPhaseDefinition(phase, definition);
  return phaseDefinition
    ? {
        sessionLifecycle: phaseDefinition.sessionLifecycle,
        recoveryMode: phaseDefinition.conformityRecoveryMode,
      }
    : undefined;
}

export function resolvePhaseConformityPolicy(
  phaseDefinition: PipelinePhaseDefinition,
): PhaseConformityPolicy {
  return {
    sessionLifecycle: phaseDefinition.sessionLifecycle,
    recoveryMode: phaseDefinition.conformityRecoveryMode,
  };
}

export function getTransitionRule(
  from: Phase,
  trigger: PhaseTransitionTrigger,
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): PhaseTransitionRule | undefined {
  return definition.transitionRules.find(
    rule => rule.from === from && rule.trigger === trigger,
  );
}

export function getPhaseLabels(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Record<Phase, string> {
  return definition.phases.reduce<Record<Phase, string>>((acc, phase) => {
    acc[phase.id] = phase.label;
    return acc;
  }, {} as Record<Phase, string>);
}

export function getApprovablePhases(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Phase[] {
  return definition.phases
    .filter(phase => phase.actions.canApprove)
    .map(phase => phase.id);
}

export function getStartablePhases(
  definition: PipelineDefinition = DEFAULT_PIPELINE_DEFINITION,
): Phase[] {
  return definition.phases
    .filter(phase => phase.actions.canStart)
    .map(phase => phase.id);
}

export function canonicalizeTaskNumber(taskNumber: string): string {
  const normalized = taskNumber.trim();
  if (!normalized || !TASK_NUMBER_PATTERN.test(normalized)) {
    return normalized;
  }

  const segments = normalized
    .split('.')
    .map(segment => Number.parseInt(segment, 10))
    .filter(segment => !Number.isNaN(segment));

  if (segments.length === 0) {
    return normalized;
  }

  while (segments.length > 1 && segments[segments.length - 1] === 0) {
    segments.pop();
  }

  return segments.join('.');
}

export function compareTaskNumbers(a: string, b: string): number {
  const canonicalA = canonicalizeTaskNumber(a);
  const canonicalB = canonicalizeTaskNumber(b);
  const aValid = TASK_NUMBER_PATTERN.test(canonicalA);
  const bValid = TASK_NUMBER_PATTERN.test(canonicalB);

  if (aValid && bValid) {
    const aSegments = canonicalA
      .split('.')
      .map(segment => Number.parseInt(segment, 10));
    const bSegments = canonicalB
      .split('.')
      .map(segment => Number.parseInt(segment, 10));
    const maxLength = Math.max(aSegments.length, bSegments.length);

    for (let index = 0; index < maxLength; index += 1) {
      const aValue = aSegments[index] ?? 0;
      const bValue = bSegments[index] ?? 0;
      if (aValue !== bValue) {
        return aValue - bValue;
      }
    }
  } else if (aValid !== bValid) {
    return aValid ? -1 : 1;
  }

  return canonicalA.localeCompare(canonicalB);
}

// Claude usage reliability contracts shared by Control Plane and local assets.
export * from './claude-usage';
export * from './claude-usage-recovery';
export * from './control-plane-usage-reliability';
export * from './session-binding';
export * from './session-observability';

// Vocabulário e política de acesso (fonte única de backend e interface)
export * from './rbac';

// Provider-agnostic SCM types (multi-provider git)
export * from './scm';
export * from './boards';

// Worker protocol types
export * from './worker-protocol';
export * from './phase-conformity';
export * from './phase-artifact-path';
export * from './skill-vault';
export * from './skill-shortcuts';
